import React, { useEffect } from 'react';
import { EIconTypes } from 'constants/Icons';
import Button from 'components/atoms/Button';
import { EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import MenuLogoOpen from 'assets/images/menu-logo-open.png';
import MenuLogoClosed from 'assets/images/menu-logo-closed.png';
import Icon from 'components/atoms/Icon/Icon';
import { Grid, List } from '@mui/material';
import { IMenuItem, MenuItems } from 'constants/MenuItems';
import { Counter } from 'components/atoms/Counter';
import { NavLink, useLocation } from 'react-router-dom';
import { MenuProfileItem } from 'components/molecules/MenuProfileItem';
import { LanguageSelect } from 'components/molecules/LanguageSelect';
import { usePermission } from 'hooks/usePermission';
import { useTranslations } from 'hooks/useTranslations';
import { useNotifications } from 'pages/NotificationsPage/hooks';
import { useDrawerClicked } from 'state/contexts/drawer';
import { useNotificationCounter } from 'models/notificationCounterContext';
import { useWeaponLicenceModule } from 'hooks/useWeaponLicenceModule';
import LicenceStore from 'state/LicenceStore';
import { colors } from 'theme/colors';
import {
  DrawerHeader,
  DrawerImageWrapper,
  StyledDrawer,
  StyledListItem,
  StyledListItemButton,
  StyledListItemIcon,
  StyledListItemText,
} from './styles';
import SearchFilterStore from 'state/SearchFilterStore';

interface IDrawer {
  open: boolean;
  setOpen: (arg: boolean) => void;
  mobile?: boolean;
}

const MyNavLink = React.forwardRef<any, any>((props, ref) => {
  const { pathname } = useLocation();
  const { to, className, activeClassName, items, onClick, children } = props;

  let isActiveItems = false;
  if (items) {
    isActiveItems = items.some((item: string) => pathname.startsWith(item));
  }

  return (
    <NavLink
      ref={ref}
      to={to}
      className={({ isActive }) =>
        `${className} ${isActive || isActiveItems ? activeClassName : ''}`
      }
      onClick={onClick}
    >
      {children}
    </NavLink>
  );
});

MyNavLink.displayName = 'MyNavLink';

const Drawer = ({ open, setOpen, mobile = false }: IDrawer) => {
  const { hasPermissions } = usePermission();
  const { hasWeaponLicenceModule } = useWeaponLicenceModule();
  const { t } = useTranslations();
  const { getNotificationUnreadCounterData, notificationsCounter } = useNotifications();
  const { setDrawerClicked } = useDrawerClicked();
  const { counter, setCounter } = useNotificationCounter();

  useEffect(() => {
    (async function initNotificationCounter() {
      await getNotificationUnreadCounterData();
    })();
  }, []);

  useEffect(() => {
    const refreshTime = process.env.NOTIFICATION_COUNTER_REFRESH_TIME_MS
      ? parseInt(process.env.NOTIFICATION_COUNTER_REFRESH_TIME_MS, 10)
      : 5000;
    const counterInterval = setInterval(async () => {
      await getNotificationUnreadCounterData();
    }, refreshTime);
    return () => clearInterval(counterInterval);
  }, [counter]);

  useEffect(() => {
    setCounter(notificationsCounter);
  }, [notificationsCounter]);

  return (
    <StyledDrawer variant="permanent" open={open} mobile={mobile ? 1 : 0}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div>
          <DrawerHeader>
            {mobile ? (
              <Button
                variant={EButtonVariants.contained}
                icon={EIconTypes.close}
                sizePX={EButtonSizesPX.px32}
                onClick={() => setOpen(!open)}
                sx={{
                  mt: 2,
                  mr: 2,
                  backgroundColor: `${colors.primary900} !important`,
                  '& > svg': { fill: colors.white },
                }}
              />
            ) : (
              <Button
                variant={EButtonVariants.contained}
                icon={open ? EIconTypes.left : EIconTypes.right}
                onClick={() => {
                  setOpen(!open);
                }}
                sx={{
                  backgroundColor: `${colors.white} !important`,
                  border: `1px solid ${colors.primary50}`,
                  '& > svg': { fill: colors.primary900 },
                }}
              />
            )}
          </DrawerHeader>
          <DrawerImageWrapper open={open} isMobile={mobile}>
            <img src={open ? MenuLogoOpen : MenuLogoClosed} alt="Logo" />
          </DrawerImageWrapper>
          {mobile && <LanguageSelect isVisible mobile />}
        </div>
        <List
          style={{ marginTop: '16px', padding: 0, flex: '1 1 auto', minHeight: 0 }}
          sx={{
            mt: open ? 0 : 8,
            overflowY: mobile ? 'visible' : 'auto',
            overflowX: 'hidden',
          }}
        >
          <Grid style={{ boxSizing: 'border-box', padding: '0 10px' }}>
            {MenuItems.map(
              (item: IMenuItem) =>
                (!item.permissions ||
                  hasPermissions(item.permissions, item?.permissionOrCondition)) &&
                (!item.requiredWeaponLicenceModule || hasWeaponLicenceModule()) && (
                  <StyledListItem
                    id={item.id}
                    component={MyNavLink}
                    to={item.url}
                    key={item.label}
                    disablePadding
                    open={open}
                    activeClassName="active-list-item"
                    mobile={mobile}
                    items={item?.items}
                    onClick={() => {
                      LicenceStore.clearStore();
                      SearchFilterStore.clearAll();
                    }}
                  >
                    <StyledListItemButton
                      id={item.id}
                      open={open}
                      onClick={
                        mobile
                          ? () => {
                              setOpen(!open);
                              setDrawerClicked(true);
                            }
                          : () => {
                              setDrawerClicked(true);
                            }
                      }
                    >
                      <StyledListItemIcon
                        sx={{
                          mr: open ? 1.5 : '0',
                        }}
                      >
                        <Icon
                          fontSize={20}
                          type={
                            item.isNotificationItem && counter
                              ? EIconTypes.notificationOn
                              : item.icon
                          }
                        />
                      </StyledListItemIcon>
                      <StyledListItemText
                        primary={t(item.label)}
                        sx={{ opacity: open ? 1 : 0, display: open ? 'block' : 'none' }}
                      />
                      {item.isNotificationItem && counter ? (
                        <Counter label={counter} open={open} />
                      ) : null}
                    </StyledListItemButton>
                  </StyledListItem>
                ),
            )}
          </Grid>
        </List>
        <MenuProfileItem open={open} mobile={mobile} close={() => setOpen(false)} />
      </div>
    </StyledDrawer>
  );
};

export { Drawer };
